import { create } from "zustand";
import { persist } from "zustand/middleware";

// Resolve a theme ("light" | "dark" | "system") to a concrete class on <html>.
// Exported so non-persisted callers (e.g. the embed page, which follows the
// host site's theme) can drive it directly.
export const applyTheme = (theme) => {
  const root = window.document.documentElement;

  root.classList.remove("light", "dark");

  if (theme === "system") {
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
    root.classList.add(systemTheme);
  } else {
    root.classList.add(theme);
  }
};

// When embedded, the host site can force the color scheme via a `colorScheme`
// query param so the embed matches their light/dark mode. This override is
// applied but never persisted to the store.
export const getColorSchemeOverride = () => {
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get("colorScheme");
  return value === "light" || value === "dark" || value === "system"
    ? value
    : null;
};

export const useThemeStore = create(
  persist(
    (set, get) => ({
      theme: "system", // 'light', 'dark', or 'system'

      setTheme: (theme) => {
        set({ theme });
        applyTheme(theme);
      },

      toggleTheme: () => {
        const current = get().theme;
        const next = current === "light" ? "dark" : "light";
        set({ theme: next });
        applyTheme(next);
      },

      initTheme: () => {
        // A host-provided color scheme (embed mode) wins over the persisted
        // preference so the embed follows the third-party site's theme.
        const override = getColorSchemeOverride();
        applyTheme(override || get().theme);
      },
    }),
    {
      name: "eScribe-theme",
    },
  ),
);

// Listen for system theme changes
if (typeof window !== "undefined") {
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", () => {
      const override = getColorSchemeOverride();
      const theme = override || useThemeStore.getState().theme;
      if (theme === "system") {
        applyTheme("system");
      }
    });
}

export default useThemeStore;
